import { useState, useMemo, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import type { QueueMessage, QueueMessageStatus } from '@/mocks/adminCabinaQueue';
import { queueMessages, detectedTypeColors, detectedTypeLabels, queueStatusColors, queueStatusLabels } from '@/mocks/adminCabinaQueue';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { LoadingSkeleton } from '@/components/base/LoadingSkeleton';
import { ErrorState } from '@/components/base/ErrorState';

type QueueFilter = 'todos' | 'no-leidos' | 'favoritos' | 'fijados' | 'pendientes';

const filterConfig: Record<QueueFilter, { label: string; icon: string }> = {
  todos: { label: 'Todos', icon: 'ri-inbox-line' },
  'no-leidos': { label: 'No leídos', icon: 'ri-mail-unread-line' },
  favoritos: { label: 'Favoritos', icon: 'ri-star-line' },
  fijados: { label: 'Fijados', icon: 'ri-pushpin-line' },
  pendientes: { label: 'Pendientes', icon: 'ri-time-line' },
};

const priorityConfig: Record<string, { label: string; color: string; icon: string }> = {
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700 border-red-200', icon: 'ri-alert-fill' },
  alta: { label: 'Alta', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: 'ri-arrow-up-circle-fill' },
  normal: { label: 'Normal', color: 'bg-foreground-100 text-foreground-600 border-foreground-200', icon: 'ri-circle-line' },
};

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
  showConfirmButton: false,
  showCloseButton: true,
});

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

function showActionsModal(
  msg: QueueMessage,
  handlers: {
    onMoveUp: (m: QueueMessage) => void;
    onMoveDown: (m: QueueMessage) => void;
    onToggleFavorite: (m: QueueMessage) => void;
    onReadAir: (m: QueueMessage) => void;
    onMarkAttended: (m: QueueMessage) => void;
    onMarkPending: (m: QueueMessage) => void;
    onDelete: (m: QueueMessage) => void;
    onBlockUser: (m: QueueMessage) => void;
    onBanIP: (m: QueueMessage) => void;
  },
) {
  const actionBtn = (icon: string, label: string, color: string, hoverBg: string) =>
    `<button type="button" data-action="${label.toLowerCase().replace(/\s/g, '-')}"
      class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-left transition-colors cursor-pointer ${color} ${hoverBg}">
      <span class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style="background:currentColor;opacity:0.1">
        <i class="${icon} text-base" style="color:currentColor"></i>
      </span>
      ${label}
    </button>`;

  const actions: string[] = [];
  actions.push(actionBtn('ri-arrow-up-s-line', 'Mover arriba', 'text-foreground-700', 'hover:bg-background-100'));
  actions.push(actionBtn('ri-arrow-down-s-line', 'Mover abajo', 'text-foreground-700', 'hover:bg-background-100'));
  actions.push(actionBtn(msg.isFavorited ? 'ri-star-line' : 'ri-star-fill', msg.isFavorited ? 'Quitar favorito' : 'Marcar favorito', 'text-amber-600', 'hover:bg-amber-50'));
  actions.push(actionBtn('ri-broadcast-line', 'Leer al aire', 'text-emerald-600', 'hover:bg-emerald-50'));
  actions.push(actionBtn('ri-check-double-line', 'Marcar atendido', 'text-foreground-600', 'hover:bg-background-100'));
  actions.push(actionBtn('ri-time-line', 'Marcar pendiente', 'text-orange-600', 'hover:bg-orange-50'));
  actions.push('<div class="border-t border-background-200 my-1"></div>');
  actions.push(actionBtn('ri-delete-bin-line', 'Eliminar', 'text-red-600', 'hover:bg-red-50'));
  actions.push(actionBtn('ri-user-unfollow-line', 'Bloquear usuario', 'text-red-600', 'hover:bg-red-50'));
  actions.push(actionBtn('ri-global-line', 'Banear IP', 'text-red-600', 'hover:bg-red-50'));

  const typeColor = detectedTypeColors[msg.detectedType];

  sharedSwal.fire({
    title: msg.senderName,
    html: `
      <div class="text-left space-y-1">
        <p class="text-xs text-foreground-500 mb-2 line-clamp-3">${escapeHtml(msg.content)}</p>
        <div class="flex items-center gap-2 mb-3 flex-wrap">
          <span class="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[10px] font-semibold ${typeColor.bg} ${typeColor.text}">
            <i class="${typeColor.icon} text-[9px]"></i>${detectedTypeLabels[msg.detectedType]}
          </span>
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${priorityConfig[msg.priority].color}">
            <i class="${priorityConfig[msg.priority].icon} text-[9px]"></i>${priorityConfig[msg.priority].label}
          </span>
        </div>
        ${actions.join('')}
      </div>
    `,
    customClass: { popup: 'rounded-2xl max-w-sm', title: 'font-bold text-sm', htmlContainer: '!p-0' },
    didOpen: () => {
      const popup = Swal.getPopup();
      if (!popup) return;
      popup.querySelector('[data-action="mover-arriba"]')?.addEventListener('click', () => { Swal.close(); handlers.onMoveUp(msg); });
      popup.querySelector('[data-action="mover-abajo"]')?.addEventListener('click', () => { Swal.close(); handlers.onMoveDown(msg); });
      popup.querySelector('[data-action="marcar-favorito"]')?.addEventListener('click', () => { Swal.close(); handlers.onToggleFavorite(msg); });
      popup.querySelector('[data-action="quitar-favorito"]')?.addEventListener('click', () => { Swal.close(); handlers.onToggleFavorite(msg); });
      popup.querySelector('[data-action="leer-al-aire"]')?.addEventListener('click', () => { Swal.close(); handlers.onReadAir(msg); });
      popup.querySelector('[data-action="marcar-atendido"]')?.addEventListener('click', () => { Swal.close(); handlers.onMarkAttended(msg); });
      popup.querySelector('[data-action="marcar-pendiente"]')?.addEventListener('click', () => { Swal.close(); handlers.onMarkPending(msg); });
      popup.querySelector('[data-action="eliminar"]')?.addEventListener('click', () => { Swal.close(); handlers.onDelete(msg); });
      popup.querySelector('[data-action="bloquear-usuario"]')?.addEventListener('click', () => { Swal.close(); handlers.onBlockUser(msg); });
      popup.querySelector('[data-action="banear-ip"]')?.addEventListener('click', () => { Swal.close(); handlers.onBanIP(msg); });
    },
  });
}

export default function QueueTabContent() {
  const [messages, setMessages] = useState<QueueMessage[]>(queueMessages);
  const [activeFilter, setActiveFilter] = useState<QueueFilter>('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [pageState, setPageState] = useState<'loading' | 'ready' | 'error'>('loading');

  const loadData = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.07;
      setPageState(shouldError ? 'error' : 'ready');
    }, 600);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredMessages = useMemo(() => {
    return messages.filter((m) => {
      if (activeFilter === 'no-leidos') return m.queueStatus === 'nuevo';
      if (activeFilter === 'favoritos') return (m.isFavorited || m.queueStatus === 'favorito') && m.queueStatus !== 'archivado' && m.queueStatus !== 'bloqueado';
      if (activeFilter === 'fijados') return (m.isPinned || m.queueStatus === 'fijado') && m.queueStatus !== 'archivado' && m.queueStatus !== 'bloqueado';
      if (activeFilter === 'pendientes') return m.queueStatus === 'pendiente';
      if (m.queueStatus === 'archivado' || m.queueStatus === 'bloqueado') return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return m.senderName.toLowerCase().includes(q) || m.content.toLowerCase().includes(q) || (m.songRequest?.toLowerCase().includes(q));
      }
      return true;
    });
  }, [messages, activeFilter, searchQuery]);

  const counts = useMemo(() => ({
    todos: messages.filter((m) => m.queueStatus !== 'archivado' && m.queueStatus !== 'bloqueado').length,
    'no-leidos': messages.filter((m) => m.queueStatus === 'nuevo').length,
    favoritos: messages.filter((m) => (m.isFavorited || m.queueStatus === 'favorito') && m.queueStatus !== 'archivado' && m.queueStatus !== 'bloqueado').length,
    fijados: messages.filter((m) => (m.isPinned || m.queueStatus === 'fijado') && m.queueStatus !== 'archivado' && m.queueStatus !== 'bloqueado').length,
    pendientes: messages.filter((m) => m.queueStatus === 'pendiente').length,
  }), [messages]);

  const stats = useMemo(() => {
    const enCola = messages.filter((m) => (m.isPinned || m.isFavorited) && m.queueStatus !== 'leido_aire' && m.queueStatus !== 'bloqueado' && m.queueStatus !== 'archivado').length;
    return { enCola, total: messages.length };
  }, [messages]);

  const handleMoveUp = useCallback((msg: QueueMessage) => {
    setMessages((prev) => { const idx = prev.findIndex((m) => m.id === msg.id); if (idx <= 0) return prev; const arr = [...prev]; [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]; return arr; });
    SwalAlert.toast('Movido arriba', 'success');
  }, []);

  const handleMoveDown = useCallback((msg: QueueMessage) => {
    setMessages((prev) => { const idx = prev.findIndex((m) => m.id === msg.id); if (idx < 0 || idx >= prev.length - 1) return prev; const arr = [...prev]; [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]; return arr; });
    SwalAlert.toast('Movido abajo', 'success');
  }, []);

  const handleMarkAttended = useCallback((msg: QueueMessage) => {
    setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, queueStatus: 'leido_aire' as QueueMessageStatus, readAtAir: new Date().toISOString(), isPinned: false } : m));
    SwalAlert.toast('Marcado como atendido', 'success');
  }, []);

  const handleMarkPending = useCallback((msg: QueueMessage) => {
    setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, queueStatus: 'pendiente' as QueueMessageStatus } : m));
    SwalAlert.toast('Marcado como pendiente', 'info');
  }, []);

  const handleToggleFavorite = useCallback((msg: QueueMessage) => {
    const willBeFav = !msg.isFavorited;
    setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, isFavorited: !m.isFavorited, queueStatus: (!m.isFavorited && m.queueStatus === 'nuevo') ? 'favorito' as QueueMessageStatus : m.queueStatus } : m));
    SwalAlert.toast(willBeFav ? 'Favorito' : 'Quitado de favoritos', willBeFav ? 'success' : 'info');
  }, []);

  const handleReadAir = useCallback(async (msg: QueueMessage) => {
    const result = await SwalAlert.confirm('¿Leer al aire?', `El mensaje de "${msg.senderName}" será marcado como leído al aire.`);
    if (!result.isConfirmed) return;
    setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, queueStatus: 'leido_aire' as QueueMessageStatus, readAtAir: new Date().toISOString(), isPinned: false } : m));
    SwalAlert.toast('Leído al aire', 'success');
  }, []);

  const handleDelete = useCallback(async (msg: QueueMessage) => {
    const result = await Swal.fire({ title: '<span class="text-foreground-950">¿Eliminar de la cola?</span>', html: `<p class="text-sm text-foreground-600">El mensaje de <strong>${msg.senderName}</strong> será eliminado permanentemente.</p>`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Eliminar', cancelButtonText: 'Cancelar', confirmButtonColor: '#ef4444', cancelButtonColor: '#d1d5db', customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap cursor-pointer', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap cursor-pointer' }, buttonsStyling: false });
    if (!result.isConfirmed) return;
    setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    SwalAlert.toast('Eliminado', 'error');
  }, []);

  const handleBlockUser = useCallback(async (msg: QueueMessage) => {
    const result = await Swal.fire({ title: '<span class="text-foreground-950">¿Bloquear usuario?</span>', html: `<p class="text-sm text-foreground-600"><strong>${msg.senderName}</strong> será bloqueado del chat. Todos sus mensajes serán marcados como bloqueados.</p>`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Bloquear usuario', cancelButtonText: 'Cancelar', confirmButtonColor: '#ef4444', cancelButtonColor: '#d1d5db', customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap cursor-pointer', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap cursor-pointer' }, buttonsStyling: false });
    if (!result.isConfirmed) return;
    setMessages((prev) => prev.map((m) => m.senderName === msg.senderName ? { ...m, queueStatus: 'bloqueado' as QueueMessageStatus, isPinned: false, isFavorited: false } : m));
    SwalAlert.toast(`${msg.senderName} bloqueado`, 'warning');
  }, []);

  const handleBanIP = useCallback(async (msg: QueueMessage) => {
    const result = await Swal.fire({ title: '<span class="text-foreground-950">¿Banear IP?</span>', html: `<p class="text-sm text-foreground-600">La IP <code class="bg-background-200 px-1.5 py-0.5 rounded text-xs font-mono">${msg.ip}</code> de <strong>${msg.senderName}</strong> será bloqueada permanentemente.</p>`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Banear IP', cancelButtonText: 'Cancelar', confirmButtonColor: '#ef4444', cancelButtonColor: '#d1d5db', customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap cursor-pointer', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap cursor-pointer' }, buttonsStyling: false });
    if (!result.isConfirmed) return;
    setMessages((prev) => prev.map((m) => m.ip === msg.ip ? { ...m, queueStatus: 'bloqueado' as QueueMessageStatus, isPinned: false, isFavorited: false } : m));
    SwalAlert.toast(`IP ${msg.ip} baneada`, 'error');
  }, []);

  const handleDragStart = (e: React.DragEvent, msg: QueueMessage) => { e.dataTransfer.setData('text/plain', String(msg.id)); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e: React.DragEvent, msg: QueueMessage) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId(msg.id); };
  const handleDragLeave = () => setDragOverId(null);
  const handleDrop = (e: React.DragEvent, target: QueueMessage) => {
    e.preventDefault(); setDragOverId(null);
    const draggedId = Number(e.dataTransfer.getData('text/plain'));
    if (draggedId === target.id) return;
    setMessages((prev) => { const arr = [...prev]; const fromIdx = arr.findIndex((m) => m.id === draggedId); const toIdx = arr.findIndex((m) => m.id === target.id); if (fromIdx < 0 || toIdx < 0) return prev; const [item] = arr.splice(fromIdx, 1); arr.splice(toIdx, 0, item); return arr; });
    SwalAlert.toast('Reordenado', 'success');
  };

  const formatTime = (iso: string) => new Date(iso).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' });
  const filters: QueueFilter[] = ['todos', 'no-leidos', 'favoritos', 'fijados', 'pendientes'];

  if (pageState === 'loading') return <div className="p-4 md:p-8"><LoadingSkeleton variant="table" rows={6} cols={4} /></div>;
  if (pageState === 'error') return <div className="flex items-center justify-center py-20"><ErrorState title="Error al cargar la cola" message="No pudimos obtener los mensajes de la cola del locutor. Intentá de nuevo." onRetry={loadData} /></div>;

  return (
    <div className="flex flex-col h-full">
      {/* Filters + Search */}
      <div className="shrink-0 px-4 md:px-8 py-3 border-b border-background-200 bg-background-50">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none flex-1 min-w-0">
            {filters.map((f) => {
              const isActive = activeFilter === f;
              const cfg = filterConfig[f];
              let activeClass = 'bg-secondary-500 text-background-50';
              if (f === 'favoritos') activeClass = 'bg-amber-500 text-background-50';
              if (f === 'pendientes') activeClass = 'bg-orange-500 text-background-50';
              if (f === 'no-leidos') activeClass = 'bg-primary-500 text-background-50';
              if (f === 'fijados') activeClass = 'bg-violet-500 text-background-50';
              return (
                <button key={f} onClick={() => setActiveFilter(f)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all shrink-0 ${isActive ? activeClass : 'bg-background-100 text-foreground-500 hover:bg-background-200'}`}>
                  <i className={`${cfg.icon} text-[11px]`}></i>{cfg.label}
                  {counts[f] > 0 && <span className={`ml-0.5 px-1.5 py-0 rounded-full text-[9px] font-bold ${isActive ? 'bg-background-50/30 text-background-50' : 'bg-background-200 text-foreground-500'}`}>{counts[f]}</span>}
                </button>
              );
            })}
          </div>
          <div className="relative shrink-0 w-full sm:w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Buscar..." className="w-full pl-9 pr-8 py-2 rounded-xl bg-background-100 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none transition-all placeholder:text-foreground-400" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400 transition-colors"><i className="ri-close-line text-background-50 text-[8px]"></i></button>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {filteredMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4"><i className="ri-inbox-line text-foreground-300 text-2xl"></i></div>
            <p className="text-sm font-bold text-foreground-400">Cola vacía</p>
            <p className="text-[11px] text-foreground-400 mt-1">No hay mensajes en esta sección</p>
          </div>
        ) : (
          <div>
            {/* Mobile cards */}
            <div className="lg:hidden divide-y divide-background-100">
              {filteredMessages.map((msg) => (
                <div key={msg.id} className="px-4 py-3 hover:bg-background-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-background-200 overflow-hidden shrink-0 mt-0.5">
                      <img src={msg.senderAvatar} alt={msg.senderName} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-semibold text-foreground-900">{msg.senderName}</span>
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${detectedTypeColors[msg.detectedType].bg} ${detectedTypeColors[msg.detectedType].text}`}>
                          <i className={`${detectedTypeColors[msg.detectedType].icon} text-[8px]`}></i>{detectedTypeLabels[msg.detectedType]}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold border ${priorityConfig[msg.priority].color}`}>
                          <i className={`${priorityConfig[msg.priority].icon} text-[8px]`}></i>{priorityConfig[msg.priority].label}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">{msg.content}</p>
                      {msg.songRequest && <p className="text-[10px] text-violet-600 font-medium mt-1"><i className="ri-music-fill text-[9px] mr-0.5"></i>{msg.songRequest}</p>}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${queueStatusColors[msg.queueStatus].bg} ${queueStatusColors[msg.queueStatus].text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${queueStatusColors[msg.queueStatus].dot}`}></span>{queueStatusLabels[msg.queueStatus]}
                        </span>
                        <span className="text-[10px] text-foreground-400">{formatTime(msg.timestamp)}</span>
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); showActionsModal(msg, { onMoveUp: handleMoveUp, onMoveDown: handleMoveDown, onToggleFavorite: handleToggleFavorite, onReadAir: handleReadAir, onMarkAttended: handleMarkAttended, onMarkPending: handleMarkPending, onDelete: handleDelete, onBlockUser: handleBlockUser, onBanIP: handleBanIP }); }} className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors shrink-0">
                      <i className="ri-more-2-fill text-base"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden lg:block">
              <div className="sticky top-0 z-10 grid grid-cols-[36px_80px_1fr_120px_80px_48px] px-4 md:px-8 py-2.5 bg-background-100 border-b border-background-200 text-[10px] font-bold text-foreground-500 uppercase tracking-wider">
                <div className="flex items-center justify-center"><i className="ri-draggable text-xs"></i></div>
                <div>Prioridad</div><div>Mensaje</div><div>Usuario</div><div>Estado</div><div></div>
              </div>
              <div className="divide-y divide-background-100">
                {filteredMessages.map((msg) => {
                  const isDragOver = dragOverId === msg.id;
                  const typeColor = detectedTypeColors[msg.detectedType];
                  const priority = priorityConfig[msg.priority];
                  return (
                    <div key={msg.id} draggable onDragStart={(e) => handleDragStart(e, msg)} onDragOver={(e) => handleDragOver(e, msg)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, msg)} className={`grid grid-cols-[36px_80px_1fr_120px_80px_48px] px-4 md:px-8 py-3 items-start cursor-pointer transition-all group ${isDragOver ? 'bg-secondary-50 ring-2 ring-secondary-300 ring-inset rounded-lg' : 'hover:bg-background-50'}`}>
                      <div className="flex items-center justify-center pt-0.5"><div className="w-5 h-5 rounded flex items-center justify-center text-foreground-400 opacity-30 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"><i className="ri-draggable text-sm"></i></div></div>
                      <div className="flex items-center pt-0.5"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${priority.color}`}><i className={`${priority.icon} text-[9px]`}></i>{priority.label}</span></div>
                      <div className="min-w-0 pr-2">
                        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${typeColor.bg} ${typeColor.text}`}><i className={`${typeColor.icon} text-[8px]`}></i>{detectedTypeLabels[msg.detectedType]}</span>
                          {msg.isFavorited && <i className="ri-star-fill text-amber-400 text-[10px] shrink-0"></i>}
                          {msg.isPinned && <i className="ri-pushpin-fill text-violet-500 text-[10px] shrink-0"></i>}
                        </div>
                        <p className="text-xs leading-relaxed line-clamp-2 text-foreground-700">{msg.content}</p>
                        {msg.songRequest && <p className="text-[10px] text-violet-600 font-medium mt-0.5"><i className="ri-music-fill text-[9px] mr-0.5"></i>{msg.songRequest}</p>}
                      </div>
                      <div className="flex items-center gap-2 pt-0.5">
                        <div className="w-7 h-7 rounded-full bg-background-200 overflow-hidden shrink-0"><img src={msg.senderAvatar} alt={msg.senderName} className="w-full h-full object-cover" loading="lazy" /></div>
                        <div className="min-w-0"><p className="text-[11px] font-semibold text-foreground-800 truncate">{msg.senderName}</p></div>
                      </div>
                      <div className="flex items-center pt-0.5"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${queueStatusColors[msg.queueStatus].bg} ${queueStatusColors[msg.queueStatus].text}`}><span className={`w-1.5 h-1.5 rounded-full ${queueStatusColors[msg.queueStatus].dot}`}></span>{queueStatusLabels[msg.queueStatus]}</span></div>
                      <div className="flex items-center justify-center pt-0.5" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => showActionsModal(msg, { onMoveUp: handleMoveUp, onMoveDown: handleMoveDown, onToggleFavorite: handleToggleFavorite, onReadAir: handleReadAir, onMarkAttended: handleMarkAttended, onMarkPending: handleMarkPending, onDelete: handleDelete, onBlockUser: handleBlockUser, onBanIP: handleBanIP })} className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors" title="Acciones"><i className="ri-more-2-fill text-base"></i></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}